import {gsap} from "gsap/all";

// The thrusters burning out of the back of a ship: the flight to Mars in Path to
// Mars, the three of the heat shield in EDL.
//
// Same idea as EDL's ShipHeatGlow but much smaller: one canvas instead of two, no
// smoke washing back over the hull, and a single warm palette. Stacking soft sprites
// gives the boiling edge of a flame for a few dozen drawImage calls a frame, where a
// real blur filter would cost most of the kiosk's frame budget.
//
// The canvas lives inside the .ship box, so it follows whatever the flight does to
// that box: the arc, the rotation into the travel direction, the scale up at launch.
// Nothing here reads where the ship is or which way it points.
//
// Everything is measured in fractions of the canvas, so the whole effect is sized
// from CSS. Change the canvas box in game.scss and the thrusters follow.
export class ThrusterGlow {

  // Takes the canvas itself, or a box holding one. Anything measured below can be
  // overridden through options, which is how a second ship gets its own nozzle
  // placement and reach without a second copy of all this.
  constructor($ship, options = {}) {
    this.$canvas = $ship.is("canvas") ? $ship : $ship.find("canvas.ship-thruster");
    this.ctx = this.$canvas.length ? this.$canvas.get(0).getContext("2d") : null;

    // Where the engine sits in the canvas, as a fraction of it. The ship art points
    // up and its nozzle is the small bell near the bottom of the hull, so the
    // thrusters hang from there and run down the rest of the canvas. Sat a little
    // below the nozzle so the burn clears the ship art and the engine stays readable.
    this.nozzleX = 0.485;
    this.nozzleY = 0.322;

    // How far down the canvas a puff travels before it dies at full throttle, as a
    // fraction of the canvas height. Against the canvas box in game.scss that works
    // out to a bit over the ship's own length at most, which is as far as the burn
    // should reach. The longest lived puffs go a little further, see reach, and a
    // part throttle keeps only some of this, see idleKeep.
    this.thrusterLength = 0.37;

    // Puffs per second at full throttle, and how long each one lives.
    this.emitPerSecond = 120;
    this.lifeMin = 0.22;
    this.lifeMax = 0.45;

    // Puff size as a fraction of the canvas width, at birth and at death. It shrinks
    // as it falls away, which tapers the thrusters into a teardrop instead of letting
    // them flare into a cone.
    this.sizeStart = 0.30;
    this.sizeEnd = 0.12;

    // How far a puff wanders sideways over its life, as a fraction of the canvas
    // width, so the edge never reads as a straight line.
    this.spread = 0.07;

    // The steady core on the nozzle, under the puffs. Without it the thrusters would
    // wink out whenever the puffs thin out.
    this.coreSize = 0.38;
    this.coreAlpha = 0.5;

    // Hottest to coolest. A puff walks down this list as it ages, which turns white
    // hot into a dim glow with no color math per frame.
    this.palette = ["#FFFFFF", "#F4EEE6", "#FBBE7E", "#EE8F3E"];

    // Above one device pixel per CSS pixel buys nothing on something this soft.
    this.maxPixelRatio = 1.5;

    Object.assign(this, options);

    this.sprites = this.buildSprites();
    this.particles = [];

    // Everything drawn is scaled off this, so setting the throttle is a single tween
    // on one number and a full burn is just it sitting at 1.
    this.intensity = 0;

    // What the lowest burn keeps of a full one, per property. The throttle reads as
    // the length of the flame, not its brightness: dimming instead made low settings
    // vanish, which looks like the engine failing rather than idling. So reach comes
    // down hard and the rest barely move, keeping a small burn a solid bright little
    // flame. All four are 1 at full throttle, so a real burn is untouched.
    this.idleKeep = {
      reach: 0.25,
      emit: 0.6,
      size: 0.7,
      alpha: 0.75,
    };

    // How the throttle travels when it is lit or eased to another setting.
    this.enterEase = "power2.out";

    this.running = false;
    this.emitCarry = 0;
    this.elapsed = 0;
    this.enterTimeline = null;

    this.tick = this.tick.bind(this);
    this.sizeCanvas = this.sizeCanvas.bind(this);

    window.addEventListener("resize", this.sizeCanvas);

    this.sizeCanvas();
    this.reset();
  }

  // One soft blob per palette color, drawn once and stamped over and over. Tinting
  // them up front means a puff is never recolored at draw time, it just picks the
  // sprite matching how far it has cooled.
  buildSprites() {
    return this.palette.map((color) => {
      const size = 64;
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;

      const ctx = canvas.getContext("2d");
      const gradient = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);

      gradient.addColorStop(0, color);
      gradient.addColorStop(0.4, this.withAlpha(color, 0.5));
      gradient.addColorStop(1, this.withAlpha(color, 0));

      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, size, size);

      return canvas;
    });
  }

  withAlpha(hex, alpha) {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  // Match the backing store to the canvas CSS box. offsetWidth/Height instead of a
  // bounding rect, because the flight scales and rotates the ship and those are the
  // untransformed sizes, which is what we want.
  sizeCanvas() {
    if (!this.ctx) return;

    const canvas = this.$canvas.get(0);
    const width = canvas.offsetWidth;
    const height = canvas.offsetHeight;
    if (!width || !height) return;

    const ratio = Math.min(window.devicePixelRatio || 1, this.maxPixelRatio);
    const backingWidth = Math.round(width * ratio);
    const backingHeight = Math.round(height * ratio);

    // Writing the backing store size wipes the canvas, so it is only written
    // when it actually changes. start() sizes on every call, including the ones
    // that only retune the throttle, and resizing there blanked a burning engine
    // for a frame.
    if (this.width && canvas.width === backingWidth && canvas.height === backingHeight) return;

    this.ratio = ratio;
    this.width = backingWidth;
    this.height = backingHeight;

    canvas.width = backingWidth;
    canvas.height = backingHeight;
  }

  // Blend between a full burn and what idle keeps of it, at the current throttle.
  atThrottle(keep) {
    return keep + (1 - keep) * this.intensity;
  }

  // How brightly the engine burns now. Barely moves across the throttle range, since
  // length carries it, but it still has to reach 0 when the engine goes out or a fade
  // would leave a flame sitting on the canvas.
  burnAlpha() {
    return this.intensity > 0 ? this.atThrottle(this.idleKeep.alpha) : 0;
  }

  spawn() {
    this.particles.push({
      // Off the nozzle by a hair so the puffs are not all born on the same pixel.
      offsetX: (Math.random() - 0.5) * 0.06,
      offsetY: Math.random() * 0.03,
      // Sideways drift, picked once and applied over the whole life.
      driftX: (Math.random() - 0.5) * this.spread,
      // How far down this one gets, so they do not all die in a flat line.
      reach: 0.7 + Math.random() * 0.5,
      age: 0,
      life: this.lifeMin + Math.random() * (this.lifeMax - this.lifeMin),
      size: 0.8 + Math.random() * 0.45,
      alpha: 0.2 + Math.random() * 0.2,
      // How fast it gives up its heat, so they never cool in step.
      cool: 0.8 + Math.random() * 0.5,
    });
  }

  tick(time, deltaMS) {
    if (!this.running) return;

    const dt = Math.min(deltaMS / 1000, 0.05);
    this.elapsed += dt;

    if (this.intensity > 0) {
      this.emitCarry += this.emitPerSecond * this.atThrottle(this.idleKeep.emit) * dt;
      while (this.emitCarry >= 1) {
        this.emitCarry -= 1;
        this.spawn();
      }
    }

    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      p.age += dt;
      if (p.age >= p.life) this.particles.splice(i, 1);
    }

    this.draw();
  }

  draw() {
    if (!this.ctx || !this.width) return;

    this.ctx.clearRect(0, 0, this.width, this.height);
    // Added on top of each other instead of painted over each other, so the
    // thrusters go white hot on their own where the puffs pile up near the nozzle.
    this.ctx.globalCompositeOperation = "lighter";

    this.drawCore();
    this.drawParticles();
  }

  // The glow on the nozzle itself, breathing slightly so the engine never looks
  // frozen.
  drawCore() {
    const breath = 1 + Math.sin(this.elapsed * 9) * 0.09;
    const point = {x: this.nozzleX, y: this.nozzleY};

    const size = this.coreSize * breath * this.atThrottle(this.idleKeep.size);
    const alpha = this.coreAlpha * this.burnAlpha();

    this.stamp(this.sprites[1], point, size, alpha);
    this.stamp(this.sprites[0], point, size * 0.45, alpha * 0.9);
  }

  drawParticles() {
    // Worked out once a frame rather than per puff: the throttle is the same for all
    // of them.
    const reach = this.thrusterLength * this.atThrottle(this.idleKeep.reach);
    const sizeScale = this.atThrottle(this.idleKeep.size);
    const alphaScale = this.burnAlpha();

    this.particles.forEach((p) => {
      const t = p.age / p.life;

      // Up fast, down slow, so a puff flares as it leaves the nozzle and takes its
      // time dying.
      const fade = Math.sin(Math.PI * Math.pow(t, 0.5));

      const cooled = Math.min(t * p.cool, 1);
      const spriteIndex = Math.min(Math.floor(cooled * this.palette.length), this.palette.length - 1);

      const travel = t * reach * p.reach;
      const point = {
        x: this.nozzleX + p.offsetX + p.driftX * t,
        y: this.nozzleY + p.offsetY + travel,
      };

      const size = p.size * (this.sizeStart + (this.sizeEnd - this.sizeStart) * t) * sizeScale;

      this.stamp(this.sprites[spriteIndex], point, size, fade * p.alpha * alphaScale);
    });
  }

  // point is in fractions of the canvas, size in fractions of its width.
  stamp(sprite, point, size, alpha) {
    if (alpha <= 0.002) return;

    const drawn = size * this.width;
    const x = point.x * this.width;
    const y = point.y * this.height;

    this.ctx.globalAlpha = Math.min(alpha, 1);
    this.ctx.drawImage(sprite, x - drawn / 2, y - drawn / 2, drawn, drawn);
  }

  // Take the engine to a throttle setting and hold it there: `level` is how hard it
  // burns, 1 being everything it has. Works from cold or from another setting alike,
  // so easing a full burn into a cruise is the same call as lighting up. Returns the
  // timeline so the caller can fold it into a longer sequence. ease replaces the
  // usual one, for a throttle travelling over a long stretch at a steady pace.
  start(durationSec = 0.6, level = 1, ease = this.enterEase) {
    if (!this.ctx) return null;

    this.killTimeline();
    // Size now, not at boot: the screen may not have been laid out when this was
    // built, and the canvas would have measured zero.
    this.sizeCanvas();
    this.startAmbient();

    this.enterTimeline = gsap.timeline();
    this.enterTimeline
      .to(this.$canvas, {
        autoAlpha: 1,
        duration: durationSec * 0.5,
        ease: "power2.out"
      }, 0)
      .to(this, {
        intensity: Math.max(0, Math.min(1, level)),
        duration: durationSec,
        ease: ease
      }, 0);

    return this.enterTimeline;
  }

  // The thrusters keep running underneath while they fade, which reads better than
  // a freeze. The throttle comes down first so the flame pulls back to a nub and
  // only then goes, which reads as the engine shutting off rather than as the burn
  // being wiped off the screen.
  stop(durationSec = 0.5) {
    if (!this.ctx) return null;

    this.killTimeline();

    const throttleSec = durationSec * 0.65;

    this.enterTimeline = gsap.timeline({onComplete: () => this.reset()});
    this.enterTimeline
      .to(this, {
        intensity: 0.05,
        duration: throttleSec,
        ease: "power2.in"
      }, 0)
      .to(this.$canvas, {
        autoAlpha: 0,
        duration: durationSec - throttleSec,
        ease: "power1.inOut"
      }, throttleSec);

    return this.enterTimeline;
  }

  startAmbient() {
    if (this.running) return;

    this.running = true;
    this.elapsed = 0;
    gsap.ticker.add(this.tick);
  }

  stopAmbient() {
    if (!this.running) return;

    this.running = false;
    gsap.ticker.remove(this.tick);
  }

  // Nothing showing, ready to be lit again. Also where a run that ended lands.
  reset() {
    this.stopAmbient();
    this.killTimeline();

    gsap.killTweensOf(this);
    gsap.killTweensOf(this.$canvas);

    this.intensity = 0;
    this.particles = [];
    this.emitCarry = 0;

    gsap.set(this.$canvas, {autoAlpha: 0});

    if (this.ctx && this.width) this.ctx.clearRect(0, 0, this.width, this.height);
  }

  killTimeline() {
    if (this.enterTimeline) this.enterTimeline.kill();
    this.enterTimeline = null;
  }

  kill() {
    this.reset();
    window.removeEventListener("resize", this.sizeCanvas);
  }
}
